import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAccessToken, useIsAuthenticated, useUserState } from '@features/user';
import { hooks } from 'front-chat-sdk';

import APIService from '../../../services/APIService';
import { FRONT_CHAT_CONFIG } from '../config';
import { setInitializationError, setInitializationStatus, setInitialized } from '../store';
import type { FrontChat } from '../types';
import { isValidChatBotHashResponse, retryWithBackoff } from '../utils/retry';

const { useFrontChatBoot } = hooks;

type UseFrontChatInitResult = {
  frontChat: FrontChat | undefined;
  isInitialized: boolean;
};

/**
 * Hook to handle Front Chat initialization
 */
export const useFrontChatInit = (): UseFrontChatInitResult => {
  const dispatch = useDispatch();
  const isAuthenticated = useIsAuthenticated();
  const userState = useUserState({
    skip: !isAuthenticated,
  });
  const { getAccessToken } = useAccessToken();

  const { frontChat, initialize, isInitialized } = useFrontChatBoot(document.body);

  const mounted = useRef(true);
  const latestFrontChat = useRef(frontChat);
  latestFrontChat.current = frontChat;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      latestFrontChat.current?.('shutdown');
      dispatch(setInitialized(false));
      dispatch(setInitializationStatus('idle'));
    };
  }, [dispatch]);

  const initializeChatBot = useCallback(async () => {
    if (isInitialized) {
      return;
    }

    const shouldNotInitialize = [
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      FRONT_CHAT_CONFIG.disabledEnvironments.includes(import.meta.env.VITE_BUILD_ENV),
      !userState?.userInfo,
    ];

    if (shouldNotInitialize.some(condition => condition)) {
      return;
    }

    const accessToken = await getAccessToken();
    if (!mounted.current) return;
    if (!accessToken) {
      dispatch(setInitializationError('Unable to get access token'));
      dispatch(setInitializationStatus('error'));
      return;
    }

    dispatch(setInitializationStatus('initializing'));
    dispatch(setInitializationError(null));

    try {
      const response = await retryWithBackoff(
        async () => {
          const res = await APIService.makeAPIGetRequest({
            requestString: FRONT_CHAT_CONFIG.apiEndpoints.getChatBotHash,
            accessToken: accessToken,
          });

          if (!res.ok) {
            throw new Error(res.error.message || 'Failed to fetch user hash');
          }

          return res;
        },
        FRONT_CHAT_CONFIG.maxRetryAttempts,
        FRONT_CHAT_CONFIG.retryDelay,
      );

      if (!mounted.current) return;
      const data = response.value as unknown;

      if (!isValidChatBotHashResponse(data)) {
        throw new Error('Invalid response format from chat bot hash API');
      }

      const userChatBotHash = data.chatbot_hash;

      if (initialize && userState?.userInfo) {
        initialize({
          chatId: FRONT_CHAT_CONFIG.chatId,
          email: userState.userInfo.email || '',
          name: userState.userInfo.name || '',
          userHash: userChatBotHash,
          welcomeMessageAppearance: 'hidden',
          useDefaultLauncher: false,
        });

        dispatch(setInitializationStatus('ready'));
      }
    } catch (error) {
      if (!mounted.current) return;
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize chat';
      dispatch(setInitializationError(errorMessage));
      dispatch(setInitializationStatus('error'));
    }
  }, [getAccessToken, initialize, userState, isInitialized, dispatch]);

  // Initialize after delay
  useEffect(() => {
    const timer = setTimeout(() => {
      void initializeChatBot();
    }, FRONT_CHAT_CONFIG.initDelay);

    return () => {
      clearTimeout(timer);
    };
  }, [initializeChatBot]);

  // Update Redux when initialized
  useEffect(() => {
    if (isInitialized) {
      dispatch(setInitialized(true));
    }
  }, [isInitialized, dispatch]);

  return { frontChat: frontChat as FrontChat | undefined, isInitialized };
};
